/**
 * Helpers for LoanProduct → LoanType hierarchy (post migration 40).
 *
 * Active API returns:
 *   { loan_products: [...], loan_types: [...] }
 * Compatibility: loan_types also carry parent_id (= loan_product_id).
 *
 * Same-ID repair (migration 43): a product and its LoanType may share one id.
 * Merged lists therefore can contain two rows with the same id — always prefer
 * the leaf LoanType when resolving a selection.
 */

export function mapActiveLoanTypes(payload = {}) {
  // New shape: { loan_products, loan_types }
  if (payload && (payload.loan_products || payload.loan_types)) {
    const products = (payload.loan_products || []).map((p) => ({
      id: p.id,
      name: p.product_name || p.name,
      code: p.product_code || p.code,
      loan_type_name: p.product_name || p.name,
      loan_type_code: p.product_code || p.code,
      is_product: true,
      is_root: true,
      is_leaf: false,
      parent_id: null,
    }));
    const types = (payload.loan_types || []).map((t) => ({
      id: t.id,
      name: t.loan_type_name || t.name,
      code: t.loan_type_code || t.code,
      loan_type_name: t.loan_type_name || t.name,
      loan_type_code: t.loan_type_code || t.code,
      parent_id: t.loan_product_id || t.parent_id || null,
      loan_product_id: t.loan_product_id || t.parent_id || null,
      parent_name: t.parent_name || t.loan_product_name || null,
      is_product: false,
      is_root: false,
      is_leaf: true,
    }));
    return [...products, ...types];
  }

  // Legacy flat array of loan_types with parent_id
  const list = Array.isArray(payload) ? payload : payload.loan_types || [];
  return list.map((t) => ({
    id: t.id,
    name: t.loan_type_name || t.name,
    code: t.loan_type_code || t.code,
    loan_type_name: t.loan_type_name || t.name,
    loan_type_code: t.loan_type_code || t.code,
    parent_id: t.parent_id || t.loan_product_id || null,
    loan_product_id: t.loan_product_id || t.parent_id || null,
    parent_name: t.parent_name || t.loan_product_name || null,
    is_root: !(t.parent_id || t.loan_product_id),
    is_leaf: !!(t.parent_id || t.loan_product_id),
  }));
}

/**
 * Look up a merged product/type row by id, preferring the leaf LoanType when
 * a same-ID product header is also present (migration 43).
 */
export function findSelectableLoanType(items = [], id) {
  if (!id) return null;
  const matches = items.filter((item) => item && item.id === id);
  if (!matches.length) return null;
  return matches.find((item) => item.is_leaf) || matches[0];
}

/**
 * True when selection is a LoanType under a LoanProduct (backend matrix leaf rule).
 * Same-ID types (loan_product_id === id) are valid leaves.
 */
export function isMatrixLeafSelection(item) {
  if (!item || item.is_leaf === false || item.is_product) return false;
  const parent = item.parent_id || item.loan_product_id;
  return !!parent;
}

/**
 * Grouped Matrix dropdown: product roots as non-selectable headers, children as leaves.
 * Roots with no children are omitted (cannot invent a leaf).
 */
export function buildGroupedLeafOptions(loanTypes = []) {
  const roots = loanTypes
    .filter((lt) => !lt.parent_id && !lt.loan_product_id)
    .sort((a, b) =>
      (a.loan_type_name || a.name || '').localeCompare(b.loan_type_name || b.name || '')
    );
  const byParent = {};
  loanTypes.forEach((lt) => {
    const parentKey = lt.parent_id || lt.loan_product_id;
    if (parentKey && lt.is_leaf !== false && !lt.is_product) {
      byParent[parentKey] = byParent[parentKey] || [];
      byParent[parentKey].push(lt);
    }
  });
  Object.values(byParent).forEach((list) =>
    list.sort((a, b) =>
      (a.loan_type_name || a.name || '').localeCompare(b.loan_type_name || b.name || '')
    )
  );
  const groups = [];
  const usedChildIds = new Set();
  roots.forEach((root) => {
    const children = byParent[root.id] || [];
    if (children.length === 0) return;
    children.forEach((c) => usedChildIds.add(c.id));
    groups.push({ root, children });
  });
  const orphans = loanTypes.filter(
    (lt) =>
      (lt.parent_id || lt.loan_product_id) &&
      lt.is_leaf !== false &&
      !lt.is_product &&
      !usedChildIds.has(lt.id)
  );
  if (orphans.length) {
    groups.push({
      root: { id: '__orphan__', loan_type_name: 'Other', name: 'Other' },
      children: orphans.sort((a, b) =>
        (a.loan_type_name || a.name || '').localeCompare(b.loan_type_name || b.name || '')
      ),
    });
  }
  return groups;
}

export function getRootLoanTypes(products = []) {
  return products
    .filter((p) => p.is_product || p.is_root || !p.parent_id)
    .slice()
    .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
}

export function getChildLoanTypes(products = [], parentId) {
  if (!parentId) return [];
  return products
    .filter((p) => p.parent_id === parentId && (p.is_leaf || !p.is_product))
    .slice()
    .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
}

/**
 * Resolve the leaf LoanType to submit.
 * After migration 40: Product must have Loan Types; parent-as-leaf is not allowed.
 * Legacy fallback: if mapped list still has a root without children, allow it.
 */
export function resolveLeafLoanType(products, parentId, subLoanTypeId) {
  if (!parentId) {
    return { ok: false, error: 'Please select a Loan Product' };
  }
  const parent = products.find((p) => p.id === parentId);
  if (!parent) {
    return { ok: false, error: 'Selected Loan Product is invalid' };
  }
  const children = getChildLoanTypes(products, parentId);
  if (children.length > 0) {
    if (!subLoanTypeId) {
      return { ok: false, error: 'Please select a Loan Type', requiresSub: true, children };
    }
    const child = children.find((c) => c.id === subLoanTypeId);
    if (!child) {
      return {
        ok: false,
        error: 'Selected Loan Type does not belong to the selected Loan Product',
        requiresSub: true,
        children,
      };
    }
    return { ok: true, leaf: child, requiresSub: true, children };
  }
  // No loan types under product — cannot invent; block submit
  return {
    ok: false,
    error: 'No Loan Types are configured for this Loan Product. Please contact admin.',
    requiresSub: false,
    children: [],
  };
}
